/**
 * Parse Campaign Kiosks from GraphQL to frontend format
 * Handles both paginated (edges) and non-paginated formats
 */
export const parseCampaignKiosksFromGraphQL = (kioskData) => {
  // Handle paginated response (edges structure)
  if (kioskData?.edges) {
    const kiosks = kioskData.edges.map((edge) => edge.node);
    return {
      kiosks: kiosks.map(parseKioskNode),
      totalCount: kioskData.totalCount || 0,
      pageInfo: kioskData.pageInfo || {}
    };
  }
  
  // Handle old non-paginated format (backward compatibility)
  if (Array.isArray(kioskData)) {
    return {
      kiosks: kioskData.map(parseKioskNode),
      totalCount: kioskData.length,
      pageInfo: {}
    };
  }
  
  return { kiosks: [], totalCount: 0, pageInfo: {} };
};

/**
 * Parse a single kiosk node to frontend format
 */
const parseKioskNode = (ck) => ({
  id: ck.kioskId,
  campaignKioskId: ck.id,
  name: ck.kiosk?.importKioskId,
  image: ck.kiosk?.venue?.image || null,
  installedImageUrl: ck.installedImageUrl,
  price: {
    // Create price object with different payment type keys
    installments: ck.contractPrice,
    prepaid: ck.contractPrice,
    monthly_subscription: ck.contractPrice,
    annual_subscription: ck.contractPrice,
  },
  contractPrice: ck.contractPrice,
  placementType: ck.kioskAdPlacement?.typeName,
  locationInVenue: ck.kiosk?.kioskLocation?.typeName,
  type: ck.kiosk?.kioskType?.typeName,
  isDigital: ck.kiosk?.isDigital || false,
  adDuration: ck.digitalSeconds,
  evergreenAmount: ck.evergreenPrice,
  boosted: ck.boosted,
  bumpable: ck.bumpable,
  // Reservation dates for date range calculations
  reservationStart: ck.reservationStart,
  reservationEnd: ck.reservationEnd,
  // Venue info
  venueId: ck.kiosk?.venue?.id,
  venueName: ck.kiosk?.venue?.venueName,
  city: ck.kiosk?.venue?.city,
  state: ck.kiosk?.venue?.state,
  address: ck.kiosk?.venue?.address1,
  address2: ck.kiosk?.venue?.address2,
  zip: ck.kiosk?.venue?.zip,
  venueRank: ck.kiosk?.venue?.venueRank,
  // Digital kiosk specific
  digitalTotalTime: ck.kiosk?.digitalTotalTime,
  digitalReservedTime: ck.kiosk?.digitalReservedTime,
  kioskLocationId: ck.kiosk?.kioskLocationId
});

/**
 * Parse Campaign Billing Info from GraphQL to frontend format
 */
export const parseCampaignBillingInfoFromGraphQL = (billingInfo) => {
  if (!billingInfo) return {};

  const discountPercent = billingInfo.subTotal && billingInfo.discount
    ? ((billingInfo.discount / billingInfo.subTotal) * 100).toFixed(2)
    : null;

  const costTotal = billingInfo.subTotal && billingInfo.discount
    ? (billingInfo.subTotal + billingInfo.discount).toFixed(2)
    : billingInfo.subTotal;

  return {
    subtotal: billingInfo.subTotal,
    costTotal,
    orderTotal: billingInfo.total,
    initialPaymentDue: billingInfo.downPaymentAmount,
    discount: billingInfo.discount,
    discountPercent,
    productionFee: billingInfo.productionFee,
    installmentPaymentAmount: billingInfo.monthlyPaymentAmount,
    countOfPayments: billingInfo.paymentCount,
    paymentType: billingInfo.paymentFrequencyNavigation?.typeName || null,
    billingStartDate: billingInfo.billingStartDate,
    splitPayments: billingInfo.splitPayments
  };
};

/**
 * Parse Campaign Users from GraphQL to frontend format
 */
export const parseCampaignUsersFromGraphQL = (campaignUsers) => {
  if (!Array.isArray(campaignUsers)) return {};

  const users = {};

  campaignUsers.forEach((cu) => {
    const roleKey = cu.campaignRoleType.charAt(0).toLowerCase() + cu.campaignRoleType.slice(1);
    users[roleKey] = {
      id: cu.user.id,
      firstName: cu.user.firstName,
      lastName: cu.user.lastName,
      email: cu.user.email,
      phone: cu.user.phone
    };
  });

  return users;
};

